import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Hook to track page views in Google Tag Manager (GTM) on every route change
 * Usage: Call this hook at the top level of your app (e.g., in App.js or within a layout component)
 * 
 * This enables:
 * - Page view tracking for SPA route navigation
 * - User behavior analytics (time on page, bounce rate, etc.)
 * - Custom event tracking via window.trackGAEvent()
 */
export const usePageViewTracking = () => {
  const location = useLocation();

  useEffect(() => {
    // Wait for gtag to be available (loaded from GTM script)
    if (typeof window !== 'undefined' && window.gtag) {
      // Track pageview for the current route
      window.gtag('config', 'AW-10806099481', {
        page_path: location.pathname + location.search,
        page_title: document.title || 'ezybill',
      });
    }
  }, [location.pathname, location.search]);
};

/**
 * Track custom events in Google Analytics
 * Usage: window.trackGAEvent('event_name', { param1: 'value1', param2: 'value2' })
 * 
 * Examples:
 *  - window.trackGAEvent('invoice_created', { currency: 'INR', value: 1000 })
 *  - window.trackGAEvent('payment_received', { method: 'cash' })
 */
export const initGoogleAnalyticsEventTracking = () => {
  if (typeof window !== 'undefined') {
    window.trackGAEvent = (eventName, eventData = {}) => {
      if (window.gtag) {
        window.gtag('event', eventName, eventData);
      }
    };
  }
};

export default usePageViewTracking;
